import type { ReactNode } from "react";

import { Button, ChipGroup, ChipMultiGroup } from "../../../ui";

interface FilterButton {
  value: string;
  label: string;
}

interface ButtonGroup {
  filters: string;
  onChange: (event: unknown, value: string) => void;
  label?: string;
  buttons: FilterButton[];
}

interface ActionButton {
  label?: string;
  variant?: "text" | "outlined" | "contained";
  color?: "primary" | "secondary" | "error" | "info" | "success" | "warning";
  startIcon?: ReactNode;
  onClick?: () => void;
  disabled?: boolean;
  loading?: boolean;
  sx?: unknown;
  children: ReactNode;
}

interface TableControlsProps {
  buttons?: FilterButton[];
  filters?: string | string[];
  onChange?: (event: unknown, value: string) => void;
  onClear?: () => void;
  additionalFilters?: ReactNode[];
  actions?: ActionButton[];
  elevation?: number;
  borderRadius?: string;
  backgroundColor?: string;
  loading?: boolean;
  buttonGroups?: ButtonGroup[];
  syncButton?: ActionButton;
  fullscreenButton?: ActionButton;
  exportButton?: ActionButton;
  insuranceReportButton?: ActionButton;
  tpFoldersReportButton?: ActionButton;
}

const variantOf = (variant?: ActionButton["variant"]) => {
  if (variant === "outlined") return "secondary" as const;
  if (variant === "text") return "ghost" as const;
  return "primary" as const;
};

function Action({
  action,
  fallbackVariant,
}: {
  action: ActionButton;
  fallbackVariant?: ActionButton["variant"];
}) {
  return (
    <Button
      variant={
        action.color === "error"
          ? "danger"
          : variantOf(action.variant ?? fallbackVariant ?? "outlined")
      }
      size="sm"
      onClick={action.onClick}
      disabled={action.disabled || action.loading}
    >
      {action.loading ? (
        <span
          className="ui-loading__spinner"
          style={{ width: 12, height: 12, borderWidth: 2 }}
        />
      ) : (
        action.startIcon
      )}
      {action.children}
    </Button>
  );
}

/**
 * Filter/action bar shared by the list pages. Filters are the reference's
 * chip row; actions are the reference's buttons.
 */
const TableControls = ({
  buttons,
  filters,
  onChange,
  onClear,
  additionalFilters = [],
  actions = [],
  loading = false,
  buttonGroups = [],
  syncButton,
  fullscreenButton,
  exportButton,
  insuranceReportButton,
  tpFoldersReportButton,
}: TableControlsProps) => {
  return (
    <div className="ui-toolbar" style={{ gap: 12 }}>
      {buttons && buttons.length > 0 && (
        Array.isArray(filters) ? (
          <ChipMultiGroup
            values={filters}
            onChange={(values) => onChange?.(null, values as unknown as string)}
            size="sm"
            options={buttons.map((button) => [button.value, button.label])}
          />
        ) : (
          <ChipGroup
            value={filters ?? buttons[0]?.value ?? ""}
            onChange={(value) => onChange?.(null, value)}
            size="sm"
            options={buttons.map((button) => [button.value, button.label])}
          />
        )
      )}

      {buttonGroups.map((group, index) => (
        <ChipGroup
          key={index}
          value={group.filters}
          onChange={(value) => group.onChange(null, value)}
          size="sm"
          options={group.buttons.map((button) => [button.value, button.label])}
        />
      ))}

      {additionalFilters.map((filter, index) => (
        <div key={index}>{filter}</div>
      ))}

      {onClear && (
        <Button variant="ghost" size="sm" onClick={onClear} disabled={loading}>
          Clear
        </Button>
      )}

      <span className="ui-toolbar__spacer" />

      <div className="ui-toolbar__group">
        {syncButton && <Action action={syncButton} />}
        {fullscreenButton && <Action action={fullscreenButton} />}
        {exportButton && <Action action={exportButton} fallbackVariant="outlined" />}
        {insuranceReportButton && (
          <Action action={insuranceReportButton} fallbackVariant="outlined" />
        )}
        {tpFoldersReportButton && (
          <Action action={tpFoldersReportButton} fallbackVariant="outlined" />
        )}
        {actions.map((action, index) => (
          <Action key={index} action={action} fallbackVariant="contained" />
        ))}
      </div>
    </div>
  );
};

export default TableControls;
